import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MigrateDates = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingPincode, setLoadingPincode] = useState(false);
  const [loadingNan, setLoadingNan] = useState(false);
  const [loadingBillStates, setLoadingBillStates] = useState(false);
  const [loadingBillAddresses, setLoadingBillAddresses] = useState(false);
  const [result, setResult] = useState(null);
  const [pincodeResult, setPincodeResult] = useState(null);
  const [nanResult, setNanResult] = useState(null);
  const [billStatesResult, setBillStatesResult] = useState(null);
  const [billAddressesResult, setBillAddressesResult] = useState(null);

  const handleMigrate = async () => {
    if (!window.confirm('This will convert all member expiry dates from mm/dd/yyyy to dd/mm/yyyy format. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/migrate-dates`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setResult(response.data);
      toast.success('Date migration completed successfully!');
    } catch (error) {
      console.error('Migration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to migrate dates');
    } finally {
      setLoading(false);
    }
  };

  const handlePincodeMigrate = async () => {
    if (!window.confirm('This will clean all pincode fields by removing .0 and keeping only 6 digits. Continue?')) {
      return;
    }

    setLoadingPincode(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/migrate-pincodes`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setPincodeResult(response.data);
      toast.success('Pincode migration completed successfully!');
    } catch (error) {
      console.error('Pincode migration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to migrate pincodes');
    } finally {
      setLoadingPincode(false);
    }
  };

  const handleNanRemoval = async () => {
    if (!window.confirm('This will remove all "nan" text from ALL databases (Members, Advertisers, Bills, Dispatches). Continue?')) {
      return;
    }

    setLoadingNan(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/remove-nan`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setNanResult(response.data);
      toast.success('NaN cleanup completed successfully!');
    } catch (error) {
      console.error('NaN removal error:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove NaN values');
    } finally {
      setLoadingNan(false);
    }
  };

  const handleBillStatesMigration = async () => {
    if (!window.confirm('This will update all Advertiser bill states from their advertiser records. Continue?')) {
      return;
    }

    setLoadingBillStates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/migrate-bill-states`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setBillStatesResult(response.data);
      toast.success('Bill states migration completed successfully!');
    } catch (error) {
      console.error('Bill states migration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to migrate bill states');
    } finally {
      setLoadingBillStates(false);
    }
  };

  const handleBillAddressesMigration = async () => {
    if (!window.confirm('This will populate all Advertiser bill address fields (address, district, state, pincode) from their advertiser records. Continue?')) {
      return;
    }

    setLoadingBillAddresses(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/migrate-bill-addresses`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setBillAddressesResult(response.data);
      toast.success('Bill addresses migration completed successfully!');
    } catch (error) {
      console.error('Bill addresses migration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to migrate bill addresses');
    } finally {
      setLoadingBillAddresses(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center space-x-4 mb-8">
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2" size={18} />
            Back
          </Button>
          <h1 className="text-4xl font-bold text-blue-600">
            Migrate Expiry Dates
          </h1>
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
          <div className="flex">
            <AlertTriangle className="h-6 w-6 text-yellow-400 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Important: Date Format Conversion</h3>
              <p className="mt-1 text-sm text-yellow-700">
                This tool will convert all member expiry dates from <strong>mm/dd/yyyy</strong> format to <strong>dd/mm/yyyy</strong> format.
              </p>
              <p className="mt-1 text-sm text-yellow-700">
                Examples:
              </p>
              <ul className="list-disc list-inside text-sm text-yellow-700 ml-4">
                <li>01/15/2025 → 15/01/2025</li>
                <li>12/31/2024 → 31/12/2024</li>
                <li>03/05/2026 → 05/03/2026</li>
              </ul>
              <p className="mt-2 text-sm text-yellow-700 font-semibold">
                Dates already in dd/mm/yyyy format will be skipped.
              </p>
            </div>
          </div>
        </div>

        {/* Date Migration Button */}
        <div className="form-section mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-600">Migrate Expiry Dates</h2>
          <p className="text-gray-600 mb-4">
            Click the button below to convert all expiry dates in your member database. This process may take a few moments depending on the number of records.
          </p>
          <Button
            onClick={handleMigrate}
            disabled={loading}
            className="w-full"
            style={{ backgroundColor: '#2563eb', color: 'white' }}
          >
            <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
            {loading ? 'Converting Dates...' : 'Convert All Dates to dd/mm/yyyy'}
          </Button>
        </div>

        {/* Pincode Migration Button */}
        <div className="form-section mb-8">
          <h2 className="text-xl font-semibold mb-4 text-green-600">Clean Pincode Data</h2>
          <p className="text-gray-600 mb-4">
            Remove .0 from all pincode fields and ensure they contain only 6 digits. This will clean both Member and Advertiser pincode data.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-700">
              <strong>Examples:</strong>
            </p>
            <ul className="list-disc list-inside text-sm text-gray-700 ml-4">
              <li>400001.0 → 400001</li>
              <li>110001.0 → 110001</li>
              <li>560001.0 → 560001</li>
            </ul>
          </div>
          <Button
            onClick={handlePincodeMigrate}
            disabled={loadingPincode}
            className="w-full"
            style={{ backgroundColor: '#059669', color: 'white' }}
          >
            <RefreshCw className={`mr-2 ${loadingPincode ? 'animate-spin' : ''}`} size={18} />
            {loadingPincode ? 'Cleaning Pincodes...' : 'Clean All Pincode Data'}
          </Button>
        </div>

        {/* Remove NaN Button */}
        <div className="form-section mb-8">
          <h2 className="text-xl font-semibold mb-4 text-red-600">Remove "nan" Text</h2>
          <p className="text-gray-600 mb-4">
            Remove all "nan", "none", "null" text from ALL databases. This will clean empty fields that show "nan" text instead of being blank.
          </p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-700 font-semibold mb-2">
              This will scan all databases:
            </p>
            <ul className="list-disc list-inside text-sm text-red-700 ml-4">
              <li>Members Database</li>
              <li>Advertisers Database</li>
              <li>Bills Database</li>
              <li>Dispatches Database</li>
            </ul>
            <p className="text-sm text-red-700 mt-2">
              All fields containing "nan", "none", or "null" will be replaced with empty strings.
            </p>
          </div>
          <Button
            onClick={handleNanRemoval}
            disabled={loadingNan}
            className="w-full"
            style={{ backgroundColor: '#dc2626', color: 'white' }}
          >
            <RefreshCw className={`mr-2 ${loadingNan ? 'animate-spin' : ''}`} size={18} />
            {loadingNan ? 'Removing NaN...' : 'Remove All "nan" Text'}
          </Button>
        </div>

        {/* Bill States Migration Button */}
        <div className="form-section mb-8">
          <h2 className="text-xl font-semibold mb-4 text-purple-600">Fix Bill States from Advertisers</h2>
          <p className="text-gray-600 mb-4">
            Update billing_state for all advertiser bills by looking up the advertiser's state. This fixes bills that have incorrect states.
          </p>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-purple-700 font-semibold mb-2">
              How it works:
            </p>
            <ul className="list-disc list-inside text-sm text-purple-700 ml-4">
              <li>Find all bills with type "Advertiser"</li>
              <li>Look up each advertiser's state from entity_id</li>
              <li>Update bill's billing_state to match advertiser's state</li>
            </ul>
            <p className="text-sm text-purple-700 mt-2">
              <strong>Example:</strong> Bill 1373 for ATADV282 (Haryana) will be updated to billing_state: "Haryana"
            </p>
          </div>
          <Button
            onClick={handleBillStatesMigration}
            disabled={loadingBillStates}
            className="w-full"
            style={{ backgroundColor: '#9333ea', color: 'white' }}
          >
            <RefreshCw className={`mr-2 ${loadingBillStates ? 'animate-spin' : ''}`} size={18} />
            {loadingBillStates ? 'Migrating Bill States...' : 'Fix All Bill States'}
          </Button>
        </div>

        {/* Bill States Results */}
        {billStatesResult && (
          <div className="form-section mb-8">
            <div className="flex items-center mb-4">
              <CheckCircle className="h-6 w-6 text-purple-500 mr-2" />
              <h2 className="text-xl font-semibold text-purple-600">Bill States Migration Complete</h2>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                <div>
                  <p className="text-gray-600">Bills Updated:</p>
                  <p className="text-2xl font-bold text-purple-700">{billStatesResult.updated_count}</p>
                </div>
                <div>
                  <p className="text-gray-600">Bills Skipped:</p>
                  <p className="text-2xl font-bold text-yellow-600">{billStatesResult.skipped_count}</p>
                </div>
                <div>
                  <p className="text-gray-600">Errors:</p>
                  <p className="text-2xl font-bold text-red-600">{billStatesResult.error_count}</p>
                </div>
              </div>
            </div>

            {billStatesResult.errors && billStatesResult.errors.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-700 font-semibold mb-2">
                  Issues Found:
                </p>
                <ul className="text-sm text-yellow-700 list-disc list-inside">
                  {billStatesResult.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4 p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-700 font-semibold mb-2">
                ✓ Bill states updated from advertiser data!
              </p>
              <p className="text-sm text-green-700">
                All advertiser bills now have correct billing states matching their advertiser records.
              </p>
            </div>
          </div>
        )}

        {/* Bill Addresses Migration Button */}
        <div className="form-section mb-8">
          <h2 className="text-xl font-semibold mb-4 text-indigo-600">Populate Bill Addresses from Advertisers</h2>
          <p className="text-gray-600 mb-4">
            Automatically populate address, district, state, and pincode for all advertiser bills from their advertiser records. This fixes bills with missing address information.
          </p>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-indigo-700 font-semibold mb-2">
              This migration will populate:
            </p>
            <ul className="list-disc list-inside text-sm text-indigo-700 ml-4">
              <li><strong>billing_address</strong> - Full advertiser address</li>
              <li><strong>district</strong> - Advertiser's district</li>
              <li><strong>billing_state</strong> - Advertiser's state</li>
              <li><strong>pincode</strong> - Advertiser's pincode (cleaned, 6 digits)</li>
            </ul>
            <p className="text-sm text-indigo-700 mt-2">
              <strong>Example:</strong> Bill for ATADV282 will get all address fields from ATADV282's advertiser record
            </p>
          </div>
          <Button
            onClick={handleBillAddressesMigration}
            disabled={loadingBillAddresses}
            className="w-full"
            style={{ backgroundColor: '#4f46e5', color: 'white' }}
          >
            <RefreshCw className={`mr-2 ${loadingBillAddresses ? 'animate-spin' : ''}`} size={18} />
            {loadingBillAddresses ? 'Populating Addresses...' : 'Populate All Bill Addresses'}
          </Button>
        </div>

        {/* Bill Addresses Results */}
        {billAddressesResult && (
          <div className="form-section mb-8">
            <div className="flex items-center mb-4">
              <CheckCircle className="h-6 w-6 text-indigo-500 mr-2" />
              <h2 className="text-xl font-semibold text-indigo-600">Bill Addresses Migration Complete</h2>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                <div>
                  <p className="text-gray-600">Bills Updated:</p>
                  <p className="text-2xl font-bold text-indigo-700">{billAddressesResult.updated_count}</p>
                </div>
                <div>
                  <p className="text-gray-600">Bills Skipped:</p>
                  <p className="text-2xl font-bold text-yellow-600">{billAddressesResult.skipped_count}</p>
                </div>
                <div>
                  <p className="text-gray-600">Errors:</p>
                  <p className="text-2xl font-bold text-red-600">{billAddressesResult.error_count}</p>
                </div>
              </div>
            </div>

            {billAddressesResult.errors && billAddressesResult.errors.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-700 font-semibold mb-2">
                  Issues Found:
                </p>
                <ul className="text-sm text-yellow-700 list-disc list-inside">
                  {billAddressesResult.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4 p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-700 font-semibold mb-2">
                ✓ Bill address fields populated from advertiser data!
              </p>
              <p className="text-sm text-green-700">
                All advertiser bills now have complete address information (address, district, state, pincode).
              </p>
            </div>
          </div>
        )}

        {/* NaN Removal Results */}
        {nanResult && (
          <div className="form-section mb-8">
            <div className="flex items-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-500 mr-2" />
              <h2 className="text-xl font-semibold text-green-600">NaN Cleanup Complete</h2>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <p className="text-gray-600">Members Cleaned:</p>
                  <p className="text-2xl font-bold text-green-700">{nanResult.members_updated}</p>
                </div>
                <div>
                  <p className="text-gray-600">Advertisers Cleaned:</p>
                  <p className="text-2xl font-bold text-green-700">{nanResult.advertisers_updated}</p>
                </div>
                <div>
                  <p className="text-gray-600">Bills Cleaned:</p>
                  <p className="text-2xl font-bold text-green-700">{nanResult.bills_updated}</p>
                </div>
                <div>
                  <p className="text-gray-600">Dispatches Cleaned:</p>
                  <p className="text-2xl font-bold text-green-700">{nanResult.dispatches_updated}</p>
                </div>
              </div>
              <div className="text-center pt-4 border-t border-green-300">
                <p className="text-gray-600">Total Records Cleaned:</p>
                <p className="text-3xl font-bold text-green-700">{nanResult.total_updated}</p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700 font-semibold mb-2">
                ✓ All "nan" text removed successfully!
              </p>
              <p className="text-sm text-blue-700">
                Empty fields are now truly empty. Future imports will also keep empty fields empty.
              </p>
            </div>
          </div>
        )}

        {/* Pincode Results */}
        {pincodeResult && (
          <div className="form-section mb-8">
            <div className="flex items-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-500 mr-2" />
              <h2 className="text-xl font-semibold text-green-600">Pincode Cleanup Complete</h2>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Members Updated:</p>
                  <p className="text-2xl font-bold text-green-700">{pincodeResult.members_updated}</p>
                </div>
                <div>
                  <p className="text-gray-600">Advertisers Updated:</p>
                  <p className="text-2xl font-bold text-green-700">{pincodeResult.advertisers_updated}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Updated:</p>
                  <p className="text-2xl font-bold text-green-700">{pincodeResult.total_updated}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                ✓ All pincode fields cleaned successfully. Pincodes now contain only 6 digits without decimals.
              </p>
            </div>
          </div>
        )}

        {/* Date Results */}
        {result && (
          <div className="form-section">
            <div className="flex items-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-500 mr-2" />
              <h2 className="text-xl font-semibold text-green-600">Date Migration Complete</h2>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Total Members:</p>
                  <p className="text-2xl font-bold text-green-700">{result.total_members}</p>
                </div>
                <div>
                  <p className="text-gray-600">Dates Updated:</p>
                  <p className="text-2xl font-bold text-green-700">{result.updated_count}</p>
                </div>
              </div>
            </div>

            {result.error_count > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-semibold text-red-800 mb-2">
                  Errors: {result.error_count}
                </h3>
                <ul className="text-sm text-red-700 space-y-1">
                  {result.errors.map((error, index) => (
                    <li key={index} className="font-mono text-xs">{error}</li>
                  ))}
                  {result.error_count > 10 && (
                    <li className="text-xs italic">... and {result.error_count - 10} more errors</li>
                  )}
                </ul>
              </div>
            )}

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-800 mb-2">Next Steps:</h3>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li>All expiry dates are now in dd/mm/yyyy format</li>
                <li>You can now edit member records without date format issues</li>
                <li>Search and filter by expiry dates will work correctly</li>
                <li>Future imports should use dd/mm/yyyy format</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MigrateDates;
